import React from 'react';
import Moment from 'moment';
import {FormattedMessage} from 'react-intl';
import {alpha, useTheme} from '@mui/material/styles';
import {Card, Alert} from 'wobi-web-common/dist/v2/index.js';
import {agentDeveloperUtils} from 'wobi-web-common/dist/components/utils/helpers';
import {destinationsToNames} from '../../utils/routingHelpers';
import {TEST_IDS} from '../../config/testIds';

function CheckIcon({size = 44}) {
  return (
    <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor'
      strokeWidth={3} strokeLinecap='round' strokeLinejoin='round'>
      <path d='M20 6L9 17l-5-5' />
    </svg>
  );
}

function DownloadIcon({size = 16}) {
  return (
    <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor'
      strokeWidth={2} strokeLinecap='round' strokeLinejoin='round'>
      <path d='M12 3v12' /><path d='M7 10l5 5 5-5' /><path d='M5 21h14' />
    </svg>
  );
}

/**
 * ConfirmationRow — label/value row inside the purchase-summary list.
 */
const ConfirmationRow = ({label, children, sub, last}) => {
  const theme = useTheme();
  return <div style={{
    display: 'flex',
alignItems: 'flex-start',
justifyContent: 'space-between',
gap: 16,
    padding: '13px 4px',
borderBottom: last ? 'none' : `1px solid ${theme.palette.divider}`,
  }}>
    <div style={{minWidth: 0}}>
      <div style={{fontSize: theme.typography.pxToRem(14.5), fontWeight: theme.typography.fontWeightBold, color: theme.palette.text.primary}}>{children}</div>
      {sub && <div style={{fontSize: theme.typography.pxToRem(11), color: theme.palette.text.secondary, marginTop: 3, lineHeight: 1.4}}>{sub}</div>}
    </div>
    <span style={{fontSize: theme.typography.pxToRem(13.5), color: theme.palette.text.secondary, flex: 'none'}}>{label}</span>
  </div>
};

const ThankYou = React.memo(({className, offer, initialTabs, ccNumber, orderId}) => {
  const theme = useTheme();
  const completeDetails = JSON.parse(sessionStorage.getItem('travel.complete-details'));

  if (!offer || !initialTabs) {
    return <div className={className} data-testid='confirmation-container' />;
  }

  const hasPeriod = Boolean(initialTabs?.policyStartDate && initialTabs?.policyEndDate);
  const days = hasPeriod ? Moment(initialTabs.policyEndDate).diff(Moment(initialTabs.policyStartDate), 'days') + 1 : null;

  return (
    <div className={className} data-testid='confirmation-container' style={{maxWidth: 640, margin: '0 auto', width: '100%'}}>
      <Card variant='floating' padding='lg' style={{textAlign: 'center'}}>
        <div style={{
          width: 84,
height: 84,
margin: '0 auto',
borderRadius: '50%',
          background: theme.palette.success.light,
color: theme.palette.success.main,
          display: 'flex',
alignItems: 'center',
justifyContent: 'center',
        }}>
          <CheckIcon size={44} />
        </div>

        <div style={{fontSize: theme.typography.pxToRem(14), color: theme.palette.text.secondary, marginTop: 20, lineHeight: 1.5}}>
          <FormattedMessage id='thank_you.tank_you'/>
        </div>
        <h1 style={{
          fontWeight: theme.typography.fontWeightBlack,
fontSize: theme.typography.pxToRem(30),
          color: theme.palette.secondary.main,
margin: '8px 0 0',
        }}>
          <FormattedMessage id='thank_you.success'/>
        </h1>
        <div style={{fontSize: theme.typography.pxToRem(15.5), color: theme.palette.text.secondary, marginTop: 8}}>
          <FormattedMessage id='thank_you.order_number_text'/>{' '}
          <b
            data-testid={TEST_IDS.thankYou.orderId}
            style={{color: theme.palette.primary.dark, direction: 'ltr', display: 'inline-block'}}
          >
            {orderId}
          </b>
        </div>

        <div style={{margin: '20px 0 4px', textAlign: 'right'}}>
          <Alert
            tone='info'
            style={{
              background: theme.palette.travel.policyPending.background,
              border: `1px solid ${theme.palette.travel.policyPending.border}`,
              borderInlineStart: `1px solid ${theme.palette.travel.policyPending.border}`,
            }}
            title={
              <span style={{color: theme.palette.travel.policyPending.title}}>
                <FormattedMessage id='thank_you.disclaimerNotActiveTitle'/>
              </span>
            }
          />
          {initialTabs?.specialCountriesApproved && (
            <div style={{marginTop: 8}}>
              <FormattedMessage id='thank_you.rescue_exclusion_disclaimer'/>
            </div>
          )}
        </div>

        <div style={{marginTop: 22, textAlign: 'right'}}>
          <div style={{fontWeight: theme.typography.fontWeightBlack, fontSize: theme.typography.pxToRem(17), color: theme.palette.secondary.main, marginBottom: 6}}>
            <FormattedMessage id='thank_you.table_title'/>
          </div>
          <div>
            {hasPeriod && (
              <ConfirmationRow label={<FormattedMessage id='thank_you.table_label_period'/>}>
                <FormattedMessage id='thank_you.table_value_period' values={{
                  start_date: Moment(initialTabs.policyStartDate).format('DD.MM.YYYY'),
                  end_date: Moment(initialTabs.policyEndDate).format('DD.MM.YYYY'),
                }}/>
              </ConfirmationRow>
            )}
            <ConfirmationRow label={<FormattedMessage id='thank_you.destination'/>}>
              {destinationsToNames(initialTabs.targetCountries)}
            </ConfirmationRow>
            {days !== null && (
              <ConfirmationRow label={<FormattedMessage id='thank_you.insurance_period'/>}>
                {days} <FormattedMessage id='step3.stayOf2'/>
              </ConfirmationRow>
            )}
            <ConfirmationRow label={<FormattedMessage id='thank_you.table_label_product_line'/>}>
              {offer.providerName}
            </ConfirmationRow>
            <ConfirmationRow label={<FormattedMessage id='thank_you.table_label_owner'/>}>
              {initialTabs.firstName1} {initialTabs.lastName1}
            </ConfirmationRow>
            <ConfirmationRow label={<FormattedMessage id='step1.tab2.gender'/>}>
              <FormattedMessage id={initialTabs.gender1 === '1' ? 'male' : 'female'}/>
            </ConfirmationRow>
            <ConfirmationRow label={<FormattedMessage id='step1.tab2.phone'/>}>
              <span style={{direction: 'ltr', display: 'inline-block'}}>{initialTabs.phone1}</span>
            </ConfirmationRow>
            <ConfirmationRow
              label={<FormattedMessage id='thank_you.table_label_price'/>}
              sub={<FormattedMessage id='thank_you.bank_of_israel_payment_info'/>}
            >
              <span style={{color: theme.palette.primary.dark}}>
                <FormattedMessage id='thank_you.table_value_price' values={{price: offer.convertedPrice}}/>
              </span>
            </ConfirmationRow>
            <ConfirmationRow label={<FormattedMessage id='thank_you.table_label_cc'/>} last>
              <span style={{direction: 'ltr', display: 'inline-block'}}>{ccNumber}*</span>
            </ConfirmationRow>
          </div>
        </div>

        <div style={{display: 'flex', justifyContent: 'center', marginTop: 18}}>
          <a
            href={`/policies/${offer.productLineId}/policy.pdf`}
            target='_blank'
            rel='noopener noreferrer'
            style={{
              display: 'inline-flex',
alignItems: 'center',
gap: 8,
              fontSize: theme.typography.pxToRem(13.5),
fontWeight: theme.typography.fontWeightBold,
color: theme.palette.secondary.dark,
              textDecoration: 'none',
border: `1px solid ${theme.palette.divider}`,
              borderRadius: 9999,
padding: '9px 16px',
            }}
          >
            <DownloadIcon size={16} /> <FormattedMessage id='thank_you.button_download'/>
          </a>
        </div>

        <p style={{fontSize: theme.typography.pxToRem(13), color: theme.palette.text.secondary, lineHeight: 1.6, margin: '24px auto 0', maxWidth: 460}}>
          <FormattedMessage id='thank_you.disclaimerNotActiveAfterCommit' values={{emailAgentTravel: agentDeveloperUtils.getAgentEmail}}/>
        </p>

        <div style={{display: 'flex', justifyContent: 'center', marginTop: 22}}>
          <a
            href={agentDeveloperUtils.serviceWebsiteUrl}
            style={{
              height: 54,
padding: '0 40px',
display: 'inline-flex',
alignItems: 'center',
justifyContent: 'center',
              border: 'none',
borderRadius: 9999,
background: theme.palette.primary.main,
color: theme.palette.primary.contrastText,
              fontWeight: theme.typography.fontWeightBold,
fontSize: theme.typography.pxToRem(17),
textDecoration: 'none',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.22)}`,
            }}
          >
            <FormattedMessage id='thank_you.service_website'/>
          </a>
        </div>
      </Card>
    </div>
  );
});

export default ThankYou;
